// socialAuthService.ts - Google/Kakao OAuth 인증
import { User } from '@/types/app_types';
import {
  BackendSocialAuthResponse,
  GoogleAuthParams,
  SocialAuthRequestParams,
  SocialAuthResult,
  StandardApiResponse,
} from '@/types/auth_types';
import axiosInstance from '@/utils/axios';
import { tokenService } from '@/utils/tokenService';
import { logger } from '@/utils/logger';
import axios from 'axios';

const PROVIDER_LABEL = (provider: 'google' | 'kakao') =>
  provider === 'google' ? 'Google' : 'Kakao';

/**
 * 공통 소셜 인증 함수
 * Google과 Kakao 인증을 통합 처리
 */
const socialAuth = async (
  params: SocialAuthRequestParams,
): Promise<StandardApiResponse<SocialAuthResult>> => {
  try {
    const response = await axiosInstance.post<BackendSocialAuthResponse>(
      params.endpoint,
      params.requestBody,
    );

    // axios interceptor가 추가한 래핑 구조 처리
    const responseData = response.data;

    if (!responseData.success || !responseData.data) {
      logger.error('소셜 인증 API 실패 응답', { provider: params.provider, message: responseData.message });
      throw new Error(responseData.message || 'Authentication failed');
    }

    const { data } = responseData;

    // 가입된 사용자
    if (data.status === 'registered' && data.access && data.refresh && data.user) {
      tokenService.setTokens({
        accessToken: data.access,
        refreshToken: data.refresh,
      });

      const user: User = {
        id: data.user.uid.toString(),
        email: data.user.email,
        name: data.user.full_name || data.user.email.split('@')[0],
        provider: params.provider,
        createdAt: data.user.created_at || new Date().toISOString(),
        updatedAt: data.user.updated_at || new Date().toISOString(),
      };

      return {
        success: true,
        message: responseData.message || `${PROVIDER_LABEL(params.provider)} 인증에 성공했습니다.`,
        data: {
          status: 'registered',
          user,
          accessToken: data.access,
          refreshToken: data.refresh,
        },
      };
    }

    // 미가입 사용자
    if (data.status === 'not_registered' && data.temp_token) {
      const notRegisteredData: {
        status: 'not_registered';
        tempToken: string;
        user?: User;
      } = {
        status: 'not_registered',
        tempToken: data.temp_token,
      };

      // 백엔드에서 user 정보를 제공하는 경우에만 포함
      if (data.user?.email) {
        notRegisteredData.user = {
          id: data.user.uid?.toString() || '',
          email: data.user.email,
          name: data.user.full_name || data.user.email.split('@')[0],
          provider: params.provider,
          createdAt: data.user.created_at || new Date().toISOString(),
          updatedAt: data.user.updated_at || new Date().toISOString(),
        };
      }

      return {
        success: true,
        message: '회원가입을 위해 약관 동의가 필요합니다.',
        data: notRegisteredData,
      };
    }

    logger.error('예상치 못한 소셜 인증 응답 형식', { provider: params.provider, data });
    throw new Error('예상치 못한 응답 형식입니다.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      logger.error('소셜 인증 axios 오류', {
        provider: params.provider,
        status: error.response?.status,
        data: error.response?.data,
      });

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        `${PROVIDER_LABEL(params.provider)} 인증에 실패했습니다.`;

      return {
        success: false,
        message: errorMessage,
        data: null,
      };
    }

    logger.error('알 수 없는 소셜 인증 오류', { provider: params.provider, error });
    const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
    return {
      success: false,
      message: `${PROVIDER_LABEL(params.provider)} 인증에 실패했습니다. ${errorMessage}`,
      data: null,
    };
  }
};

/**
 * Google 인증
 */
export const googleAuth = async (
  params: GoogleAuthParams,
): Promise<StandardApiResponse<SocialAuthResult>> => {
  return socialAuth({
    provider: 'google',
    endpoint: '/auth/google/check/',
    requestBody: {
      code: params.code,
      code_verifier: params.code_verifier,
      redirect_uri: params.redirect_uri,
    },
  });
};

/**
 * Kakao 인증
 */
export const kakaoAuth = async (
  kakaoAccessToken: string,
): Promise<StandardApiResponse<SocialAuthResult>> => {
  return socialAuth({
    provider: 'kakao',
    endpoint: '/auth/kakao/check/',
    requestBody: {
      access_token: kakaoAccessToken,
    },
  });
};

/**
 * 약관 동의 후 소셜 회원가입 - Google/Kakao 공통
 */
export const completeGoogleKakaoSignup = async (
  tempToken: string,
  fullName?: string,
): Promise<
  StandardApiResponse<{
    user: User;
    accessToken: string;
    refreshToken: string;
  }>
> => {
  try {
    const response = await axiosInstance.post('/auth/social/register/', {
      temp_token: tempToken,
      ...(fullName && { full_name: fullName }),
    });

    const { user, access, refresh } = response.data.data;

    return {
      success: true,
      message: response.data.message || '회원가입이 완료되었습니다.',
      data: {
        user: {
          id: user.uid.toString(),
          email: user.email,
          name: user.full_name,
        },
        accessToken: access,
        refreshToken: refresh,
      },
    };
  } catch (error) {
    logger.error('소셜 회원가입 완료 실패', { error });
    return {
      success: false,
      message: '소셜 회원가입에 실패했습니다.',
      data: null,
    };
  }
};

/**
 * Google 계정 연결 해제
 */
export const revokeGoogleAuth = async (userId: string): Promise<StandardApiResponse<unknown>> => {
  try {
    const { data } = await axiosInstance.post('/auth/google/revoke', { userId });

    if (data && typeof data === 'object' && 'success' in data) {
      return data as StandardApiResponse<unknown>;
    }

    return {
      success: true,
      message: '구글 연결 해제 성공',
      data: null,
    };
  } catch (error) {
    return {
      success: false,
      message: '구글 연결 해제 실패',
      data: null,
    };
  }
};

/**
 * Kakao 계정 연결 해제
 */
export const revokeKakaoAuth = async (userId: string): Promise<StandardApiResponse<unknown>> => {
  try {
    const { data } = await axiosInstance.post('/auth/kakao/revoke', { userId });

    if (data && typeof data === 'object' && 'success' in data) {
      return data as StandardApiResponse<unknown>;
    }

    return {
      success: true,
      message: '카카오 연결 해제 성공',
      data: null,
    };
  } catch (error) {
    return {
      success: false,
      message: '카카오 연결 해제 실패',
      data: null,
    };
  }
};

/**
 * 로그아웃 시 소셜 프로바이더별 추가 처리
 * @param provider - 소셜 로그인 제공자
 */
export const handleSocialLogout = async (provider: 'google' | 'kakao') => {
  if (provider === 'kakao' && window.Kakao?.Auth) {
    if (window.Kakao.Auth.getAccessToken()) {
      await new Promise<void>(resolve => {
        window.Kakao.Auth.logout(() => {
          resolve();
        });
      });
    }
  } else if (provider === 'google' && window.google?.accounts?.id) {
    window.google.accounts.id.disableAutoSelect();
    document.cookie = 'g_state=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }
};
